<template>
    <v-container fluid>
        <v-data-table
            :headers="headers"
            :items="itemsFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
            :height="$vuetify.breakpoint.mdAndDown ? '75vh' : '66vh'"
            fixed-header
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">FINISH & UC CODE</h1>
                        <v-btn
                            rounded
                            color="primary"
                            @click="openCreateItem"
                            class="mx-2"
                            v-if="editSettingsPermission"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                        <v-btn
                            rounded
                            color="success"
                            @click="$refs.fileInput.click()"
                            class="mx-2"
                            v-if="editSettingsPermission"
                        >
                            <v-icon>
                                mdi-file-upload
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'IMPORT' }}
                        </v-btn>
                        <v-btn
                            rounded
                            color="info"
                            @click="exportToExcel"
                            class="mx-2"
                            v-if="editSettingsPermission"
                        >
                            <v-icon>
                                mdi-file-download
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'EXPORT' }}
                        </v-btn>
                        <v-btn
                            rounded
                            color="error"
                            @click="openExceptionsDialog = true"
                            class="mx-2"
                            v-if="editSettingsPermission && hasExceptions"
                        >
                            <v-icon>
                                mdi-alert-circle
                            </v-icon>
                            {{
                                $vuetify.breakpoint.smAndDown
                                    ? ''
                                    : `EXCEPTIONS (${exceptionsCount})`
                            }}
                        </v-btn>
                        <input
                            ref="fileInput"
                            type="file"
                            accept=".xlsx,.xls"
                            style="display: none"
                            @change="handleFileUpload"
                        />
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.name`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByName"
                    dense
                />
            </template>
            <template v-slot:[`header.consecutive`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByConsecutive"
                    dense
                />
            </template>
            <template v-slot:[`header.supplier`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchBySupplier"
                    dense
                />
            </template>
            <template v-slot:[`header.aama`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByAama"
                    dense
                />
            </template>
            <template v-slot:[`header.covering`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByCovering"
                    dense
                />
            </template>
            <template v-slot:[`header.description`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByDescription"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.enabled`]="{ item }">
                <div class="d-flex justify-center">
                    <v-switch
                        :input-value="item.enabled !== false"
                        color="primary"
                        dense
                        hide-details
                        class="mt-0 pt-0"
                        :disabled="!editSettingsPermission || loading"
                        @change="toggleFinishEnabled(item)"
                    />
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openEditItem(item)">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn small icon @click="openDeleteItem(item)">
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--PROCESSES FORM-->
        <v-dialog
            v-model="openItemForm"
            :retain-focus="false"
            persistent
            width="600px"
        >
            <FinishAndUCCodeForm
                v-if="openItemForm"
                :createForm="createForm"
                :item="selectedItem"
                :originalItem="originalItem"
                :finishAndUCCodeSetting="setting"
                @addItem="addItem"
                @replaceItem="replaceItem"
                @close="closeEditItem"
            />
        </v-dialog>
        <!--Delete Process-->
        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5">Delete Process</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this process?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteItem">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteItem"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--Exceptions Dialog-->
        <v-dialog
            v-model="openExceptionsDialog"
            persistent
            max-width="1400px"
            :retain-focus="false"
        >
            <UcCodesExceptions
                v-if="openExceptionsDialog"
                :exceptions="setting.codeExceptions || []"
                :settingId="setting.id"
                @close="closeExceptionsDialog"
                @cleared="handleExceptionsCleared"
            />
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'
import * as XLSX from 'xlsx'

export default {
    name: 'FinishAndUCCode',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        FinishAndUCCodeForm: () =>
            import('@/components/FinishAndUCCode/FinishAndUCCodeForm.vue'),
        UcCodesExceptions: () =>
            import('@/components/FinishAndUCCode/UcCodesExceptions.vue'),
    },
    data: () => ({
        loading: false,
        items: [],
        selectedItem: {},
        originalItem: {},
        createForm: true,
        openItemForm: false,
        openDeleteWarning: false,
        openExceptionsDialog: false,
        finishAndUCCodeSetting: {},
        headers: [
            {
                text: 'UC CODE',
                value: 'name',
                sortable: false,
                width: '150',
            },
            {
                text: 'CONSECUTIVE',
                value: 'consecutive',
                sortable: false,
                width: '150',
            },
            {
                text: 'SUPPLIER',
                value: 'supplier',
                sortable: false,
                width: '150',
            },
            {
                text: 'AAMA',
                value: 'aama',
                sortable: false,
                width: '150',
            },
            {
                text: 'COVERING',
                value: 'covering',
                sortable: false,
                width: '150',
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                sortable: false,
            },
            {
                text: 'ENABLED',
                value: 'enabled',
                align: 'center',
                sortable: false,
                width: '100',
            },
        ],
        searchByName: '',
        searchByConsecutive: '',
        searchBySupplier: '',
        searchByAama: '',
        searchByCovering: '',
        searchByDescription: '',
        editSettingsPermission: false,
    }),
    async mounted() {
        try {
            const {
                data: { user },
            } = await API.getMyInfo()
            this.editSettingsPermission = user.permissions.includes(
                'editSettings'
            )
            if (this.editSettingsPermission) {
                this.headers.push({
                    text: 'ACTIONS',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                })
            }
            this.loading = true
            this.items = await API.getFinishesAndUCCodes(this.setting.id)
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        itemsFiltered() {
            const conditions = []
            if (this.searchByName) {
                conditions.push(this.filterByName)
            }
            if (this.searchByConsecutive) {
                conditions.push(this.filterByConsecutive)
            }
            if (this.searchBySupplier) {
                conditions.push(this.filterBySupplier)
            }
            if (this.searchByAama) {
                conditions.push(this.filterByAama)
            }
            if (this.searchByCovering) {
                conditions.push(this.filterByCovering)
            }
            if (this.searchByDescription) {
                conditions.push(this.filterByDescription)
            }
            const filtered =
                conditions.length > 0
                    ? this.items.filter(item =>
                          conditions.every(condition => condition(item))
                      )
                    : this.items
            return [...filtered].sort((a, b) => {
                const aDisabled = a.enabled === false ? 1 : 0
                const bDisabled = b.enabled === false ? 1 : 0
                return aDisabled - bDisabled
            })
        },
        hasExceptions() {
            return (
                this.setting.codeExceptions &&
                this.setting.codeExceptions.length > 0
            )
        },
        exceptionsCount() {
            return this.setting.codeExceptions
                ? this.setting.codeExceptions.length
                : 0
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        filterByName(item) {
            return (item.name || '')
                .toLowerCase()
                .includes(this.searchByName.toLowerCase())
        },
        filterByConsecutive(item) {
            return (item.consecutive || '')
                .toLowerCase()
                .includes(this.searchByConsecutive.toLowerCase())
        },
        filterBySupplier(item) {
            return (item.supplier || '')
                .toLowerCase()
                .includes(this.searchBySupplier.toLowerCase())
        },
        filterByAama(item) {
            return (item.aama || '')
                .toLowerCase()
                .includes(this.searchByAama.toLowerCase())
        },
        filterByCovering(item) {
            return (item.covering || '')
                .toLowerCase()
                .includes(this.searchByCovering.toLowerCase())
        },
        filterByDescription(item) {
            return (item.description || '')
                .toLowerCase()
                .includes(this.searchByDescription.toLowerCase())
        },
        async toggleFinishEnabled(item) {
            try {
                this.loading = true
                const updated = await API.updateFinishAndUCCode({
                    settingId: this.setting.id,
                    id: item.id,
                    enabled: item.enabled === false,
                })
                this.replaceItem(updated)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        replaceItem(itemToUpdate) {
            const index = this.items.findIndex(
                item => item.id == itemToUpdate.id
            )
            if (index >= 0) {
                this.items.splice(index, 1, {
                    ...this.items[index],
                    ...itemToUpdate,
                })
            }
        },
        addItem(item) {
            this.items.push(item)
            this.searchByName = ''
            this.searchByConsecutive = ''
            this.searchBySupplier = ''
            this.searchByAama = ''
            this.searchByCovering = ''
            this.searchByDescription = ''
        },
        removeItem() {
            const index = this.items.findIndex(
                item => item.id == this.selectedItem.id
            )
            if (index >= 0) {
                this.items.splice(index, 1)
            }
        },
        async deleteItem() {
            try {
                this.loading = true
                await API.deleteFinishAndUCCode({
                    settingId: this.setting.id,
                    id: this.selectedItem.id,
                })
                this.removeItem()
                this.closeDeleteItem()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteItem() {
            this.selectedItem = {}
            this.openDeleteWarning = false
        },
        openDeleteItem(item) {
            this.selectedItem = _.cloneDeep(item)
            this.openDeleteWarning = true
        },
        closeEditItem() {
            this.createForm = true
            this.selectedItem = {}
            this.originalItem = {}
            this.openItemForm = false
        },
        openEditItem(item) {
            this.createForm = false
            this.selectedItem = _.cloneDeep(item)
            this.originalItem = _.cloneDeep(item)
            this.openItemForm = true
        },
        openCreateItem() {
            this.createForm = true
            this.openItemForm = true
        },
        handleFileUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Only Excel files (.xlsx, .xls) are supported',
                })
                return
            }

            const reader = new FileReader()
            reader.onload = async e => {
                try {
                    this.loading = true
                    const data = e.target.result
                    await this.processExcel(data)

                    // Reset file input
                    this.$refs.fileInput.value = ''
                } catch (error) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message || 'Error processing file',
                    })
                } finally {
                    this.loading = false
                }
            }

            reader.readAsArrayBuffer(file)
        },
        async processExcel(data) {
            // Read the workbook
            const workbook = XLSX.read(data, { type: 'array' })

            // Get the first sheet
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]

            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' })

            if (jsonData.length === 0) {
                throw new Error('File is empty or has no data rows')
            }

            // Get headers (column names)
            const headers = Object.keys(jsonData[0]).map(h => h.toLowerCase())

            // Validate required columns
            const requiredColumns = [
                'description',
                'uc',
                'consecutive',
                'supplier',
                'aama',
                'covering',
            ]
            const missingColumns = requiredColumns.filter(
                col => !headers.includes(col)
            )

            if (missingColumns.length > 0) {
                throw new Error(
                    `Missing required columns: ${missingColumns.join(', ')}`
                )
            }

            // Process data rows
            const itemsToCreate = []
            for (const row of jsonData) {
                // Convert keys to lowercase for case-insensitive access
                const rowLower = {}
                for (const key in row) {
                    rowLower[key.toLowerCase()] = row[key]
                }

                const item = {
                    name: String(rowLower['uc'] || '').trim(),
                    consecutive: String(rowLower['consecutive'] || '').trim(),
                    supplier: String(rowLower['supplier'] || '').trim(),
                    aama: String(rowLower['aama'] || '').trim(),
                    covering: String(rowLower['covering'] || '').trim(),
                    description: String(rowLower['description'] || '').trim(),
                }

                if (item.name) {
                    itemsToCreate.push(item)
                }
            }

            // Create items in batch
            await this.createItemsInBatch(itemsToCreate)
        },
        async createItemsInBatch(itemsToCreate) {
            try {
                const result = await API.createFinishAndUCCodeBatch({
                    settingId: this.setting.id,
                    items: itemsToCreate,
                })

                // Replace all items with updated list
                if (result.allItems) {
                    this.items = result.allItems
                }

                // Update exceptions
                if (result.codeExceptions) {
                    this.$set(
                        this.setting,
                        'codeExceptions',
                        result.codeExceptions
                    )
                }

                // Show message only if there are errors
                if (result.errors && result.errors > 0) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: `Import with ${result.errors} errors. Please review the exceptions log.`,
                    })
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error creating items in batch',
                })
            }
        },
        closeExceptionsDialog() {
            this.openExceptionsDialog = false
        },
        async handleExceptionsCleared() {
            // clear exceptions reactively
            this.$set(this.setting, 'codeExceptions', [])
            this.closeExceptionsDialog()
        },
        exportToExcel() {
            try {
                // Prepare data for export
                const dataToExport = this.itemsFiltered.map(item => ({
                    UC: item.name || '',
                    CONSECUTIVE: item.consecutive || '',
                    SUPPLIER: item.supplier || '',
                    AAMA: item.aama || '',
                    COVERING: item.covering || '',
                    DESCRIPTION: item.description || '',
                    ENABLED: item.enabled === false ? 'No' : 'Yes',
                }))

                // Create a new workbook
                const wb = XLSX.utils.book_new()

                // Convert data to worksheet
                const ws = XLSX.utils.json_to_sheet(dataToExport)

                // Set column widths
                ws['!cols'] = [
                    { wch: 15 }, // UC
                    { wch: 15 }, // CONSECUTIVE
                    { wch: 20 }, // SUPPLIER
                    { wch: 15 }, // AAMA
                    { wch: 15 }, // COVERING
                    { wch: 40 }, // DESCRIPTION
                    { wch: 12 }, // ENABLED
                ]

                // Add worksheet to workbook
                XLSX.utils.book_append_sheet(wb, ws, 'Finish & UC Codes')

                // Generate file name with current date
                const date = new Date()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                const fileName = `Finish_UC_Codes_${date.getFullYear()}-${month}-${day}.xlsx`

                // Write and download file
                XLSX.writeFile(wb, fileName)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error exporting file',
                })
            }
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    position: sticky;
    z-index: 1;
    background: #eeeeee;
}
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
</style>
